import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/shared/api/client';
import { Skeleton } from './Skeleton';
import { formatFullDateTime, formatRelativeTimeWithClock } from '@/shared/lib/dateTime';

type NotificationItem = {
  id: number;
  type: string;
  title: string;
  details: string | null;
  actorId: number | null;
  actorName: string | null;
  actorAvatarUrl: string | null;
  boardId: number | null;
  boardName: string | null;
  path: string;
  unread: boolean;
  createdAt: string;
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 48px 0 0 0;
  z-index: 90;
  background: transparent;
`;

const Panel = styled.div`
  position: fixed;
  top: 48px;
  right: 24px;
  z-index: 100;
  width: 380px;
  max-height: 480px;
  display: flex;
  flex-direction: column;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.04),
    0 12px 40px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  animation: ${fadeIn} 0.16s ease;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
  background: var(--bg-primary);
`;

const Title = styled.h2`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--text-primary);
`;

const CloseBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--text-tertiary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.1s ease, color 0.1s ease;
  font-family: inherit;
  &:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }
`;

const List = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--border-subtle);
    border-radius: 3px;
  }
`;

const ItemRow = styled.div<{ $unread: boolean }>`
  display: grid;
  grid-template-columns: 8px 1fr auto;
  gap: 12px;
  padding: 10px 14px;
  transition: background 0.08s ease;
  background: ${({ $unread }) => ($unread ? 'color-mix(in srgb, var(--accent) 6%, transparent)' : 'transparent')};
  &:hover {
    background: var(--bg-hover);
  }
`;

const ItemLink = styled(Link)`
  min-width: 0;
  text-decoration: none;
  color: inherit;
  display: block;
`;

const Dot = styled.div<{ $c: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $c }) => $c};
  margin-top: 5px;
  flex-shrink: 0;
`;

const ItemBody = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const Verb = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.35;
`;

const RelTime = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: var(--text-tertiary);
  flex-shrink: 0;
`;

const Details = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.45;
  white-space: pre-wrap;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ActorMeta = styled.div`
  font-size: 11px;
  color: var(--text-tertiary);
  line-height: 1.35;
`;

const BoardTag = styled.span`
  align-self: flex-start;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 4px;
  background: var(--accent-muted);
  color: var(--accent);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Footer = styled.div`
  padding: 8px 14px 10px;
  border-top: 1px solid var(--border-subtle);
  flex-shrink: 0;
  background: var(--bg-primary);
`;

const ShowAllLink = styled(Link)`
  font-size: 12px;
  font-weight: 500;
  color: var(--accent);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  padding: 36px 20px;
  text-align: center;
  font-size: 13px;
  color: var(--text-tertiary);
  line-height: 1.5;
`;

const SkeletonWrap = styled.div`
  padding: 8px 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SkRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const actionColorMap: Record<string, string> = {
  CARD_CREATED: '#34d399',
  CARD_ASSIGNED: '#7c8cff',
  CARD_UPDATED: '#7c8cff',
  CARD_MOVED: '#f59e0b',
  CARD_ARCHIVED: '#6b7280',
  CARD_DELETED: '#e5484d',
  CARD_RESTORED: '#34d399',
  COLUMN_CREATED: '#8b5cf6',
  COLUMN_RENAMED: '#8b5cf6',
  COLUMN_UPDATED: '#8b5cf6',
  COLUMN_MOVED: '#8b5cf6',
  COLUMN_DELETED: '#e5484d',
  BOARD_UPDATED: '#06b6d4',
  COMMENT_CREATED: '#ec4899',
  COMMENT_ADDED: '#ec4899',
  SUBTASK_TOGGLED: '#34d399',
};

const actionVerbMap: Record<string, string> = {
  CARD_CREATED: 'Создана карточка',
  CARD_ASSIGNED: 'Назначение',
  CARD_UPDATED: 'Обновлена',
  CARD_MOVED: 'Перемещена',
  CARD_ARCHIVED: 'Архивирована',
  CARD_DELETED: 'Удалена карточка',
  CARD_RESTORED: 'Восстановлена из архива',
  COLUMN_CREATED: 'Создан столбец',
  COLUMN_RENAMED: 'Переименован столбец',
  COLUMN_UPDATED: 'Обновлён столбец',
  COLUMN_MOVED: 'Перемещён столбец',
  COLUMN_DELETED: 'Удалён столбец',
  BOARD_UPDATED: 'Обновлена доска',
  COMMENT_CREATED: 'Комментарий',
  COMMENT_ADDED: 'Комментарий',
  SUBTASK_TOGGLED: 'Подзадача',
};

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const FooterBtn = styled.button`
  border: none;
  background: transparent;
  color: var(--text-tertiary);
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: var(--text-primary);
  }
`;

const DeleteBtn = styled.button`
  border: none;
  background: transparent;
  color: var(--text-tertiary);
  width: 24px;
  height: 24px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: start;

  &:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }
`;

export const NotificationsDropdown: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const notificationsQuery = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => apiFetch<NotificationItem[]>('/api/notifications?limit=20&unreadOnly=true'),
    enabled: open,
    staleTime: 15_000,
  });
  const items = notificationsQuery.data ?? [];
  const loading = open && notificationsQuery.isPending;
  const unreadCount = items.filter((item) => item.unread).length;

  const markRead = async (id: number) => {
    await apiFetch(`/api/notifications/${id}/read`, { method: 'POST' });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] }),
    ]);
  };

  const markAllRead = async () => {
    await apiFetch('/api/notifications/read-all', { method: 'POST' });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] }),
    ]);
  };

  const removeNotification = async (id: number) => {
    await apiFetch(`/api/notifications/${id}`, { method: 'DELETE' });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['notifications', 'all'] }),
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] }),
    ]);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const openMyWork = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
  };

  const portal = (
    <>
      <Backdrop
        aria-hidden
        onMouseDown={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <Panel role="dialog" aria-label="Уведомления">
        <Header>
          <Title>Уведомления</Title>
          <CloseBtn type="button" onClick={onClose} aria-label="Закрыть">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M4 4l8 8M12 4L4 12" />
            </svg>
          </CloseBtn>
        </Header>

        {loading ? (
          <List>
            <SkeletonWrap>
              {Array.from({ length: 7 }).map((_, i) => (
                <SkRow key={i}>
                  <Skeleton $w="8px" $h="8px" $r="50%" />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Skeleton $w="72%" $h="12px" />
                    <Skeleton $w="100%" $h="10px" />
                    <Skeleton $w="40%" $h="10px" />
                  </div>
                </SkRow>
              ))}
            </SkeletonWrap>
          </List>
        ) : items.length === 0 ? (
          <List>
            <EmptyState>Нет новых уведомлений</EmptyState>
          </List>
        ) : (
          <List>
            {items.map((item) => {
              const color = actionColorMap[item.type] ?? '#7c8cff';
              const verb = actionVerbMap[item.type] ?? item.title;
              const detailText = item.details?.trim() ?? null;
              return (
                <ItemRow key={item.id} $unread={item.unread}>
                  <Dot $c={color} />
                  <ItemLink
                    to={item.path || '/my-work'}
                    onClick={() => {
                      onClose();
                      if (item.unread) {
                        void markRead(item.id);
                      }
                    }}
                  >
                    <ItemBody>
                      <ItemTop>
                        <Verb>{item.title || verb}</Verb>
                        <RelTime title={formatFullDateTime(item.createdAt)}>{formatRelativeTimeWithClock(item.createdAt)}</RelTime>
                      </ItemTop>
                      {detailText ? <Details>{detailText}</Details> : null}
                      {item.actorName ? <ActorMeta>{item.actorName}</ActorMeta> : null}
                      {item.boardName ? <BoardTag title={item.boardName}>{item.boardName}</BoardTag> : null}
                    </ItemBody>
                  </ItemLink>
                  <DeleteBtn
                    type="button"
                    aria-label="Удалить уведомление"
                    onClick={() => {
                      void removeNotification(item.id);
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                      <path d="M4 4l8 8M12 4L4 12" />
                    </svg>
                  </DeleteBtn>
                </ItemRow>
              );
            })}
          </List>
        )}

        <Footer>
          <FooterActions>
            <ShowAllLink to="/my-work" onClick={() => { openMyWork(); onClose(); }}>
              Показать все
            </ShowAllLink>
            {unreadCount > 0 ? (
              <FooterBtn
                type="button"
                onClick={() => {
                  void markAllRead();
                }}
              >
                Прочитать все
              </FooterBtn>
            ) : null}
          </FooterActions>
        </Footer>
      </Panel>
    </>
  );

  return typeof document !== 'undefined' ? createPortal(portal, document.body) : null;
};
